import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTelegramWebApp } from './hooks/useTelegramWebApp';
import {
  api,
  ApiError,
  type BingxPositionRow,
  type HistoryRow,
  type PositionsResponse,
  type StatusResponse,
  type TradeRow,
} from './api/client';
import { showAppAlert, showAppConfirm } from './utils/telegramDialogs';
import { positionMatchKey } from './utils/symbolMatch';
import {
  computeTradeCardMetrics,
  formatPrice,
  formatQty,
  formatUsd,
} from './utils/tradeCard';

const MAX_TP_LEVELS = 10;

export default function App() {
  const { tg, ready, initData, user, theme, haptic } = useTelegramWebApp();
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [trades, setTrades] = useState<TradeRow[]>([]);
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [bingxPositions, setBingxPositions] = useState<PositionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionId, setActionId] = useState<string | null>(null);
  const [tpEditorTrade, setTpEditorTrade] = useState<TradeRow | null>(null);
  const [tpFormSl, setTpFormSl] = useState('');
  const [tpFormLines, setTpFormLines] = useState<string[]>(['']);
  const [tpFormSaving, setTpFormSaving] = useState(false);
  const [view, setView] = useState<'open' | 'history' | 'dashboard'>('open');

  const loadAll = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const [s, t, p, h] = await Promise.all([
        api.status(initData),
        api.trades(initData),
        api.positions(initData),
        api.history(initData),
      ]);
      setStatus(s);
      setTrades(t.trades ?? []);
      setBingxPositions(p);
      setHistory(h.trades ?? []);
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.message || `HTTP ${e.status}`);
      } else {
        setError(e instanceof Error ? e.message : 'Load failed');
      }
      setStatus(null);
      setTrades([]);
      setBingxPositions(null);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [initData]);

  useEffect(() => {
    if (!ready) return;
    void loadAll();
  }, [ready, loadAll]);

  const liveTradeKeys = useMemo(
    () => new Set(trades.filter((t) => !t.isPaper).map((t) => positionMatchKey(t.symbol, t.side))),
    [trades]
  );

  const orphanPositions = useMemo(() => {
    if (!bingxPositions?.positions.length) return [];
    return bingxPositions.positions.filter(
      (p) => !liveTradeKeys.has(positionMatchKey(p.symbol, p.side))
    );
  }, [bingxPositions, liveTradeKeys]);

  const runAction = async (
    id: string,
    fn: () => Promise<{ ok: boolean; message: string }>,
    confirmMsg?: string
  ) => {
    const go = async () => {
      setActionId(id);
      haptic('light');
      try {
        const r = await fn();
        showAppAlert(tg, r.message || (r.ok ? 'OK' : 'Failed'));
        await loadAll();
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : String(e);
        showAppAlert(tg, msg);
      } finally {
        setActionId(null);
      }
    };
    if (confirmMsg) {
      showAppConfirm(tg, confirmMsg, (ok) => {
        if (ok) void go();
      });
    } else {
      void go();
    }
  };

  const symShort = (s: string) => s.replace(/\/USDT:USDT$/i, '');
  const fmtTs = (iso?: string | null) => (iso ? new Date(iso).toLocaleString() : '—');
  const historyPnl = (t: HistoryRow) =>
    typeof t.pnlUsdt === 'number'
      ? t.pnlUsdt
      : typeof t.realizedPnlUsdt === 'number'
        ? t.realizedPnlUsdt
        : typeof t.tpPnlUsdt === 'number'
          ? t.tpPnlUsdt
          : 0;

  const findLivePosition = (trade: TradeRow, snap: PositionsResponse | null): BingxPositionRow | undefined => {
    if (!snap || trade.isPaper) return undefined;
    const k = positionMatchKey(trade.symbol, trade.side);
    return snap.positions.find((p) => positionMatchKey(p.symbol, p.side) === k);
  };

  const dashboard = useMemo(() => {
    const closed = history ?? [];
    const closedLive = closed.filter((t) => !t.isPaper);
    const closedPaper = closed.filter((t) => t.isPaper);
    const analytics = closedLive;
    const pnlLive = analytics.reduce((acc, t) => acc + historyPnl(t), 0);
    const pnlPaper = closedPaper.reduce((acc, t) => acc + historyPnl(t), 0);
    const wins = analytics.filter((t) => historyPnl(t) > 0).length;
    const losses = analytics.filter((t) => historyPnl(t) < 0).length;
    const winRate = analytics.length > 0 ? wins / analytics.length : null;
    const avgPnl = analytics.length > 0 ? pnlLive / analytics.length : null;

    const openUnreal = (bingxPositions?.positions ?? []).reduce(
      (acc, p) => acc + (typeof p.unrealizedPnl === 'number' ? p.unrealizedPnl : 0),
      0
    );

    const now = new Date();
    const dayKey = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const lastNDays = (n: number) => {
      const days: { key: string; date: Date }[] = [];
      for (let i = n - 1; i >= 0; i--) {
        const d = new Date(now);
        d.setHours(0, 0, 0, 0);
        d.setDate(d.getDate() - i);
        days.push({ key: dayKey(d), date: d });
      }
      return days;
    };

    const last14 = lastNDays(14);
    const pnlByDay = new Map<string, number>();
    for (const x of analytics) {
      const ts = x.closedAt || x.cancelledAt || x.openedAt;
      if (!ts) continue;
      const d = new Date(ts);
      d.setHours(0, 0, 0, 0);
      const k = dayKey(d);
      pnlByDay.set(k, (pnlByDay.get(k) ?? 0) + historyPnl(x));
    }
    const series = last14.map((d) => ({
      key: d.key,
      label: `${d.date.getMonth() + 1}/${d.date.getDate()}`,
      pnl: pnlByDay.get(d.key) ?? 0,
    }));
    const maxAbs = Math.max(1, ...series.map((x) => Math.abs(x.pnl)));

    const symbolAgg = new Map<string, { count: number; pnl: number }>();
    for (const x of analytics) {
      const k = symShort(x.symbol);
      const cur = symbolAgg.get(k) ?? { count: 0, pnl: 0 };
      cur.count += 1;
      cur.pnl += historyPnl(x);
      symbolAgg.set(k, cur);
    }
    const topSymbols = [...symbolAgg.entries()]
      .map(([symbol, v]) => ({ symbol, ...v }))
      .sort((a, b) => Math.abs(b.pnl) - Math.abs(a.pnl))
      .slice(0, 6);

    return {
      closedCount: analytics.length,
      closedLiveCount: closedLive.length,
      closedPaperCount: closedPaper.length,
      pnlAll: pnlLive,
      pnlLive,
      pnlPaper,
      wins,
      losses,
      winRate,
      avgPnl,
      openUnreal,
      series,
      maxAbs,
      topSymbols,
    };
  }, [history, bingxPositions]);

  const openTpEditor = (trade: TradeRow) => {
    haptic('light');
    setTpEditorTrade(trade);
    setTpFormSl(trade.stopLoss != null ? String(Number(trade.stopLoss)) : '');
    const ordered = [...trade.targets].sort((a, b) => a.order - b.order);
    const lines = ordered.map((t) => String(Number(t.targetPrice)));
    if (lines.length === 0) {
      setTpFormLines(['']);
    } else {
      setTpFormLines([...lines, '']);
    }
  };

  const addTpFormRow = () => {
    haptic('light');
    setTpFormLines((prev) => (prev.length >= MAX_TP_LEVELS ? prev : [...prev, '']));
  };

  const removeTpFormRow = (index: number) => {
    haptic('light');
    setTpFormLines((prev) => (prev.length <= 1 ? prev : prev.filter((_, j) => j !== index)));
  };

  const closeTpEditor = () => {
    setTpEditorTrade(null);
    setTpFormSaving(false);
    setTpFormLines(['']);
  };

  const submitTpForm = async () => {
    if (!tpEditorTrade) return;
    const sl = parseFloat(tpFormSl.replace(',', '.'));
    const takeProfits = tpFormLines
      .map((s) => parseFloat(String(s).replace(',', '.')))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (!Number.isFinite(sl) || sl <= 0) {
      showAppAlert(tg, 'Enter a valid stop loss (positive number).');
      return;
    }
    if (takeProfits.length === 0) {
      showAppAlert(tg, 'Enter at least one take profit price.');
      return;
    }
    haptic('medium');
    setTpFormSaving(true);
    try {
      const r = await api.updateTradeTps(initData, tpEditorTrade.id, { stopLoss: sl, takeProfits });
      showAppAlert(tg, r.message || 'OK');
      closeTpEditor();
      await loadAll();
    } catch (e) {
      showAppAlert(tg, e instanceof ApiError ? e.message : String(e));
    } finally {
      setTpFormSaving(false);
    }
  };

  if (!tg) {
    return (
      <div className="app-shell" style={{ background: '#0d1117', color: '#e6edf3' }}>
        <div className="banner warn">
          Open this page inside Telegram as a <strong>Mini App</strong> (Web App button), or set{' '}
          <code>VITE_API_URL</code> and use a dev token flow.
        </div>
        <p style={{ opacity: 0.85, padding: '0 4px' }}>
          For local dev without Telegram: run the bot with <code>WEB_PANEL_ENABLED=true</code> and use Vite proxy;
          initData will be empty and API may reject — use optional <code>WEB_PANEL_DEV_BYPASS</code> on server (see
          README).
        </p>
      </div>
    );
  }

  return (
    <div
      className="app-shell"
      style={{
        background: theme.bg,
        color: theme.text,
      }}
    >
      <header className="app-header">
        <h1 className="app-title">Trading</h1>
        <button
          type="button"
          className="refresh-btn"
          style={{ background: theme.button, color: theme.buttonText }}
          onClick={() => {
            haptic('light');
            void loadAll();
          }}
          disabled={loading}
        >
          {loading ? '…' : 'Refresh'}
        </button>
      </header>

      {user && (
        <p style={{ fontSize: '0.8rem', opacity: 0.7, margin: '0 0 12px' }}>
          {user.first_name ?? user.username ?? `ID ${user.id}`}
        </p>
      )}

      {error && (
        <div className="banner err" style={{ color: theme.text }}>
          {error}
        </div>
      )}

      <section
        className="card"
        style={{
          background: `${theme.text}08`,
          borderColor: `${theme.text}18`,
        }}
      >
        {loading && !status ? (
          <p style={{ margin: 0 }}>Loading status…</p>
        ) : status ? (
          <div className="status-grid">
            <div>
              <div className="status-label">Mode</div>
              <div>{status.liveTrading ? 'Live' : 'Paper'}</div>
            </div>
            <div>
              <div className="status-label">Trading</div>
              <div>{status.tradingEnabled ? 'On' : 'Paused'}</div>
            </div>
            <div>
              <div className="status-label">Balance</div>
              <div>
                {status.balanceUsdt != null ? `${status.balanceUsdt.toFixed(2)} USDT` : '—'}
              </div>
            </div>
            <div>
              <div className="status-label">Active</div>
              <div>{status.activeTrades}</div>
            </div>
          </div>
        ) : null}
      </section>

      <div className="tabs" style={{ borderColor: `${theme.text}18` }}>
        <button
          type="button"
          className={`tab ${view === 'open' ? 'active' : ''}`}
          style={{
            background: view === 'open' ? `${theme.text}10` : 'transparent',
            color: theme.text,
          }}
          onClick={() => {
            haptic('light');
            setView('open');
          }}
        >
          Open
        </button>
        <button
          type="button"
          className={`tab ${view === 'history' ? 'active' : ''}`}
          style={{
            background: view === 'history' ? `${theme.text}10` : 'transparent',
            color: theme.text,
          }}
          onClick={() => {
            haptic('light');
            setView('history');
          }}
        >
          History
        </button>
        <button
          type="button"
          className={`tab ${view === 'dashboard' ? 'active' : ''}`}
          style={{
            background: view === 'dashboard' ? `${theme.text}10` : 'transparent',
            color: theme.text,
          }}
          onClick={() => {
            haptic('light');
            setView('dashboard');
          }}
        >
          Dashboard
        </button>
      </div>

      {view === 'dashboard' ? (
        <>
          <div className="section-head">
            <h2 style={{ fontSize: '0.95rem', margin: 0, opacity: 0.9 }}>Analytics</h2>
            <button
              type="button"
              className="clear-history-btn"
              style={{ background: 'rgba(255,80,80,0.22)', color: theme.text }}
              disabled={actionId === 'history-clear' || loading}
              onClick={() =>
                runAction(
                  'history-clear',
                  () => api.clearHistory(initData),
                  'Remove all closed/cancelled history from DB? Open positions stay. BingX will re-sync.'
                )
              }
            >
              Clear history
            </button>
          </div>

          <div className="kpi-grid">
            <div
              className="kpi-card"
              style={{ background: `${theme.text}06`, borderColor: `${theme.text}14` }}
            >
              <div className="kpi-label">Closed trades</div>
              <div className="kpi-value">{dashboard.closedCount}</div>
              <div className="kpi-sub" style={{ opacity: 0.72 }}>
                Live {dashboard.closedLiveCount} · Paper {dashboard.closedPaperCount}
              </div>
            </div>

            <div
              className="kpi-card"
              style={{ background: `${theme.text}06`, borderColor: `${theme.text}14` }}
            >
              <div className="kpi-label">Win rate</div>
              <div className="kpi-value">
                {dashboard.winRate == null ? '—' : `${Math.round(dashboard.winRate * 100)}%`}
              </div>
              <div className="kpi-sub">
                Wins {dashboard.wins} · Losses {dashboard.losses}
              </div>
            </div>

            <div
              className="kpi-card"
              style={{ background: `${theme.text}06`, borderColor: `${theme.text}14` }}
            >
              <div className="kpi-label">Realized PnL</div>
              <div className="kpi-value">
                {dashboard.pnlLive >= 0 ? '+' : ''}
                {dashboard.pnlLive.toFixed(2)} USDT
              </div>
              <div className="kpi-sub" style={{ opacity: 0.72 }}>
                BingX live · Paper {dashboard.pnlPaper >= 0 ? '+' : ''}
                {dashboard.pnlPaper.toFixed(2)}
              </div>
            </div>

            <div
              className="kpi-card"
              style={{ background: `${theme.text}06`, borderColor: `${theme.text}14` }}
            >
              <div className="kpi-label">Open unrealized</div>
              <div className="kpi-value">
                {dashboard.openUnreal >= 0 ? '+' : ''}
                {dashboard.openUnreal.toFixed(2)} USDT
              </div>
              <div className="kpi-sub">
                Balance {status?.balanceUsdt != null ? `${status.balanceUsdt.toFixed(2)} USDT` : '—'}
              </div>
            </div>
          </div>

          <section
            className="chart-card"
            style={{ background: `${theme.text}06`, borderColor: `${theme.text}14` }}
          >
            <div className="chart-title-row">
              <div className="chart-title">PnL by day (last 14 days)</div>
              <div className="chart-hint" style={{ opacity: 0.7 }}>
                BingX realized PnL · close time
              </div>
            </div>
            <div className="bar-chart" aria-label="PnL bar chart (last 14 days)">
              {dashboard.series.map((x) => {
                const h = Math.max(3, Math.round((Math.abs(x.pnl) / dashboard.maxAbs) * 130));
                const neg = x.pnl < 0;
                return (
                  <div
                    key={x.key}
                    className={`bar ${neg ? 'neg' : ''}`}
                    title={`${x.label}: ${x.pnl >= 0 ? '+' : ''}${x.pnl.toFixed(2)} USDT`}
                    style={{
                      height: `${h}px`,
                      background: neg ? 'rgba(255,90,90,0.28)' : 'rgba(120,160,255,0.32)',
                    }}
                  />
                );
              })}
            </div>
            <div className="bar-labels">
              <span>{dashboard.series[0]?.label ?? ''}</span>
              <span>{dashboard.series.at(-1)?.label ?? ''}</span>
            </div>
          </section>

          <section
            className="chart-card"
            style={{ background: `${theme.text}06`, borderColor: `${theme.text}14` }}
          >
            <div className="chart-title-row">
              <div className="chart-title">Top symbols</div>
              <div className="chart-hint">sorted by |PnL|</div>
            </div>
            {dashboard.topSymbols.length === 0 ? (
              <div className="empty" style={{ padding: '18px 10px' }}>
                Not enough history yet
              </div>
            ) : (
              <table className="split-table">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Trades</th>
                    <th>PnL</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard.topSymbols.map((x) => (
                    <tr key={x.symbol}>
                      <td style={{ fontWeight: 700 }}>{x.symbol}</td>
                      <td>{x.count}</td>
                      <td>
                        {x.pnl >= 0 ? '+' : ''}
                        {x.pnl.toFixed(2)} USDT
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      ) : view === 'open' ? (
        <>
          <h2 style={{ fontSize: '0.95rem', margin: '12px 0 6px', opacity: 0.9 }}>Open positions</h2>
      {bingxPositions ? (
        <p className="trade-meta" style={{ margin: '0 0 12px', fontSize: '0.75rem', opacity: 0.72 }}>
          BingX snapshot: {bingxPositions.updatedAt ? new Date(bingxPositions.updatedAt).toLocaleTimeString() : '—'}
          {bingxPositions.error ? ` · ${bingxPositions.error}` : ''}
        </p>
      ) : null}

      {!loading &&
        trades.length === 0 &&
        orphanPositions.length === 0 &&
        (!bingxPositions || bingxPositions.positions.length === 0) && (
        <div className="empty">No open positions</div>
      )}

      {trades.map((trade) => {
        const busy = actionId === trade.id;
        const active = ['OPEN_NO_SL_TP', 'ACTIVE', 'PARTIALLY_CLOSED'].includes(trade.status);
        const waiting = trade.status === 'OPEN_NO_SL_TP';
        const live = findLivePosition(trade, bingxPositions);
        const entry = live?.entryPrice && live.entryPrice > 0 ? live.entryPrice : Number(trade.entryPrice);
        const size = live?.size && live.size > 0 ? live.size : Number(trade.currentSize);
        const leverage = live?.leverage ?? trade.leverage ?? null;
        const retouchPct = status?.retraceTpRetouchClosePct ?? 50;
        const balanceUsdt =
          status?.balanceUsdt ??
          (trade.currentBalance != null ? Number(trade.currentBalance) : null);
        const card = computeTradeCardMetrics({
          side: trade.side,
          entry,
          size,
          stopLoss: trade.stopLoss != null ? Number(trade.stopLoss) : null,
          takeProfits: trade.targets.map((t) => ({ order: t.order, price: Number(t.targetPrice) })),
          leverage,
          balanceUsdt,
          retouchClosePct: retouchPct,
        });
        const statusByOrder = new Map(trade.targets.map((t) => [t.order, t]));
        return (
          <article
            key={trade.id}
            className="trade-card"
            style={{
              background: `${theme.text}06`,
              borderColor: waiting ? 'rgba(255, 200, 80, 0.45)' : `${theme.text}14`,
            }}
          >
            <div className="trade-head">
              <div>
                <div className="trade-symbol">
                  <span className={`side-pill ${trade.side === 'SHORT' ? 'short' : 'long'}`}>
                    {trade.side}
                  </span>{' '}
                  {symShort(trade.symbol)}
                </div>
                <div className="trade-meta">
                  {waiting ? 'Waiting SL/TP' : trade.status.replace(/_/g, ' ')}
                  {' · '}
                  {trade.isPaper ? 'PAPER' : 'LIVE'}
                  {trade.channelUsername ? ` · @${trade.channelUsername.replace(/^@/, '')}` : ''}
                  {trade.openedAt ? (
                    <span style={{ display: 'block', opacity: 0.75, marginTop: 4 }}>
                      Opened {fmtTs(trade.openedAt)}
                    </span>
                  ) : null}
                  {!trade.isPaper && !live && bingxPositions && !bingxPositions.error ? (
                    <span style={{ display: 'block', opacity: 0.75, marginTop: 4 }}>
                      No matching exchange row (check symbol / sync)
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="trade-meta trade-head-right">
                {leverage != null && leverage > 0 ? <div>{leverage}×</div> : null}
              </div>
            </div>

            <dl className="trade-stats">
              <div>
                <dt>Entry</dt>
                <dd>{formatPrice(entry)}</dd>
              </div>
              <div>
                <dt>Size</dt>
                <dd>{formatQty(size)}</dd>
              </div>
              <div>
                <dt>Notional</dt>
                <dd>{Number.isFinite(card.notional) ? formatUsd(card.notional) : '—'}</dd>
              </div>
              <div>
                <dt>Margin</dt>
                <dd>{card.margin != null ? formatUsd(card.margin) : '—'}</dd>
              </div>
              {live?.unrealizedPnl != null ? (
                <div>
                  <dt>PnL</dt>
                  <dd className={live.unrealizedPnl >= 0 ? 'pnl-pos' : 'pnl-neg'}>
                    {live.unrealizedPnl >= 0 ? '+' : ''}
                    {live.unrealizedPnl.toFixed(2)} USDT
                  </dd>
                </div>
              ) : null}
              {live?.markPrice != null ? (
                <div>
                  <dt>Mark</dt>
                  <dd>{formatPrice(live.markPrice)}</dd>
                </div>
              ) : null}
            </dl>

            <div className="trade-sl">
              {card.sl ? (
                <>
                  <strong>SL</strong> {formatPrice(card.sl.price)}
                  <span>
                    {card.sl.pct} · risk {card.sl.riskUsd}
                    {card.sl.balPct ? ` · ${card.sl.balPct}` : ''}
                  </span>
                </>
              ) : (
                <>
                  <strong>SL</strong> not set
                  {waiting ? <span>waiting for DETAILS</span> : null}
                </>
              )}
            </div>
            {trade.targets.length > 0 ? (
              <>
                <table className="tp-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Status</th>
                      <th>Price</th>
                      <th>Move</th>
                      <th>Close</th>
                    </tr>
                  </thead>
                  <tbody>
                    {card.tps.map((t) => {
                      const row = statusByOrder.get(t.order);
                      return (
                        <tr key={row?.id ?? t.order}>
                          <td>TP{t.order}</td>
                          <td>
                            {row ? (
                              <>
                                <span className={`tp-badge ${row.status}`}>{row.status}</span>
                                {row.pullbackAfterReach ? (
                                  <span className="tp-pullback" title="Pullback after first touch">
                                    ↩
                                  </span>
                                ) : null}
                              </>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td>{formatPrice(t.price)}</td>
                          <td>{t.pct}</td>
                          <td>{t.hint}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {card.rrLine ? <p className="trade-rr">R:R {card.rrLine}</p> : null}
                <p className="tp-size-legend">
                  After first touch, price must hold one price-poll delay; a fast wick is ignored. Then pullback
                  closes {retouchPct}% of current size (last TP 100%). SL stays after TP1/TP2.
                </p>
              </>
            ) : (
              <div className="tp-empty-hint">
                No take profits yet — use <strong>Add TPs</strong> below or <strong>From channel</strong>.
              </div>
            )}
            {active && (
              <div className="tp-inline-bar" style={{ borderColor: `${theme.text}18` }}>
                <div className="tp-inline-bar-copy">
                  <span className="tp-inline-bar-title">Take profits</span>
                  <span className="tp-inline-bar-sub">
                    Add TP levels, stop loss, or more targets anytime.
                  </span>
                </div>
                <button
                  type="button"
                  className="tp-inline-bar-btn"
                  style={{ background: theme.button, color: theme.buttonText }}
                  disabled={busy}
                  onClick={() => openTpEditor(trade)}
                >
                  {trade.targets.length === 0 ? 'Add TPs' : 'Add / edit TPs'}
                </button>
              </div>
            )}
            {active && (
              <div className="trade-actions">
                <button
                  type="button"
                  style={{ background: theme.button, color: theme.buttonText }}
                  disabled={busy}
                  onClick={() =>
                    runAction(trade.id, () => api.closeTrade(initData, trade.id), 'Close entire position?')
                  }
                >
                  Close
                </button>
                <button
                  type="button"
                  style={{ background: `${theme.button}cc`, color: theme.buttonText }}
                  disabled={busy}
                  onClick={() => runAction(trade.id, () => api.takeTrade(initData, trade.id, 25))}
                >
                  25%
                </button>
                <button
                  type="button"
                  style={{ background: `${theme.button}cc`, color: theme.buttonText }}
                  disabled={busy}
                  onClick={() => runAction(trade.id, () => api.takeTrade(initData, trade.id, 50))}
                >
                  50%
                </button>
                <button
                  type="button"
                  style={{ background: `${theme.text}22`, color: theme.text }}
                  disabled={busy}
                  onClick={() => runAction(trade.id, () => api.setTp(initData, trade.id))}
                >
                  From channel
                </button>
                <button
                  type="button"
                  style={{ background: 'rgba(255,80,80,0.25)', color: theme.text }}
                  disabled={busy}
                  onClick={() =>
                    runAction(
                      trade.id,
                      () => api.deleteTrade(initData, trade.id),
                      'Delete trade from DB (closes if needed)?'
                    )
                  }
                >
                  Delete
                </button>
              </div>
            )}
            {!active && (
              <div className="trade-actions">
                <button
                  type="button"
                  style={{ background: 'rgba(255,80,80,0.25)', color: theme.text }}
                  disabled={busy}
                  onClick={() =>
                    runAction(trade.id, () => api.deleteTrade(initData, trade.id), 'Remove from DB?')
                  }
                >
                  Delete DB
                </button>
              </div>
            )}
          </article>
        );
      })}

      {!loading &&
        orphanPositions.length > 0 &&
        orphanPositions.map((p, i) => (
          <article
            key={`orphan-${p.symbol}-${i}`}
            className="trade-card"
            style={{
              background: `${theme.text}06`,
              borderColor: `${theme.text}14`,
            }}
          >
            <div className="trade-head">
              <div>
                <div className="trade-symbol">
                  {symShort(p.symbol)} {p.side}
                </div>
                <div className="trade-meta">Exchange only (no matching open DB trade)</div>
              </div>
              <div className="trade-meta" style={{ textAlign: 'right' }}>
                <div>
                  Size {formatQty(p.size)} @ {formatPrice(p.entryPrice)}
                </div>
                {p.unrealizedPnl != null ? (
                  <div className={p.unrealizedPnl >= 0 ? 'pnl-pos' : 'pnl-neg'}>
                    PnL {p.unrealizedPnl >= 0 ? '+' : ''}
                    {p.unrealizedPnl.toFixed(2)} USDT
                  </div>
                ) : null}
                {p.markPrice != null ? <div>Mark {formatPrice(p.markPrice)}</div> : null}
                {p.leverage != null ? <div>{p.leverage}×</div> : null}
              </div>
            </div>
          </article>
        ))}
        </>
      ) : (
        <>
          <div className="section-head">
            <h2 style={{ fontSize: '0.95rem', margin: 0, opacity: 0.9 }}>Trade history</h2>
            <button
              type="button"
              className="clear-history-btn"
              style={{ background: 'rgba(255,80,80,0.22)', color: theme.text }}
              disabled={actionId === 'history-clear' || loading}
              onClick={() =>
                runAction(
                  'history-clear',
                  () => api.clearHistory(initData),
                  'Remove all closed/cancelled history from DB? Open positions stay. BingX will re-sync.'
                )
              }
            >
              Clear history
            </button>
          </div>
          {!loading && history.length === 0 ? <div className="empty">No closed trades yet</div> : null}
          {history.map((t) => (
            <article
              key={`h-${t.id}`}
              className="trade-card"
              style={{
                background: `${theme.text}06`,
                borderColor: `${theme.text}14`,
              }}
            >
              <div className="trade-head">
                <div>
                  <div className="trade-symbol">
                    {symShort(t.symbol)} {t.side}
                  </div>
                  <div className="trade-meta">
                    {t.status} · {t.isPaper ? 'paper' : 'live'}
                    <div style={{ opacity: 0.8, marginTop: 4 }}>
                      Opened: {fmtTs(t.openedAt)} · Closed: {fmtTs(t.closedAt || t.cancelledAt)}
                    </div>
                  </div>
                </div>
                <div className="trade-meta" style={{ textAlign: 'right' }}>
                  <div>
                    Entry {formatQty(Number(t.entrySize))} @ {formatPrice(Number(t.entryPrice))}
                  </div>
                  <div>
                    PnL {historyPnl(t) >= 0 ? '+' : ''}
                    {historyPnl(t).toFixed(2)} USDT
                    {!t.isPaper && t.pnlSource !== 'db' ? (
                      <span style={{ display: 'block', fontSize: '0.7rem', opacity: 0.72 }}>
                        {t.pnlSource === 'estimate' ? 'BingX est.' : 'BingX'}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>

              {t.targets.length > 0 ? (
                <table className="tp-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Status</th>
                      <th>Target</th>
                      <th>Exec</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...t.targets]
                      .sort((a, b) => a.order - b.order)
                      .map((x) => (
                        <tr key={x.id}>
                          <td>TP{x.order}</td>
                          <td>
                            <span className={`tp-badge ${x.status}`}>{x.status}</span>
                          </td>
                          <td>{formatPrice(Number(x.targetPrice))}</td>
                          <td style={{ opacity: 0.9 }}>
                            {x.executedPrice && x.executedSize
                              ? `${formatQty(Number(x.executedSize))} @ ${formatPrice(Number(x.executedPrice))}`
                              : '—'}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              ) : (
                <div className="tp-empty-hint">No TP rows</div>
              )}
            </article>
          ))}
        </>
      )}

      {tpEditorTrade ? (
        <div className="modal-backdrop" role="presentation" onClick={closeTpEditor}>
          <div
            className="modal-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tp-editor-title"
            style={{
              background: theme.bg,
              color: theme.text,
              borderColor: `${theme.text}22`,
              boxShadow: '0 -12px 40px rgba(0,0,0,0.4)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="tp-editor-title">
              SL / TP — {symShort(tpEditorTrade.symbol)} {tpEditorTrade.side}
            </h3>
            <p style={{ fontSize: '0.75rem', opacity: 0.8, margin: '0 0 12px', lineHeight: 1.45 }}>
              Prices are sorted automatically (LONG low→high, SHORT high→low). Up to {MAX_TP_LEVELS} take profits; empty
              rows are ignored. Use <strong>Add TP</strong> for more levels.
            </p>
            <div className="tp-form-grid">
              <label>
                Stop loss
                <input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  value={tpFormSl}
                  onChange={(e) => setTpFormSl(e.target.value)}
                  placeholder="e.g. 1.2345"
                  style={{ borderColor: `${theme.hint}55` }}
                />
              </label>
              {tpFormLines.map((val, i) => (
                <div key={i} className="tp-form-row">
                  <label className="tp-form-row-label">
                    Take profit {i + 1}
                    <input
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      value={val}
                      onChange={(e) => {
                        const next = [...tpFormLines];
                        next[i] = e.target.value;
                        setTpFormLines(next);
                      }}
                      placeholder="Price"
                      style={{ borderColor: `${theme.hint}55` }}
                    />
                  </label>
                  <button
                    type="button"
                    className="tp-row-remove"
                    style={{ color: theme.text, borderColor: `${theme.text}33` }}
                    disabled={tpFormSaving || tpFormLines.length <= 1}
                    onClick={() => removeTpFormRow(i)}
                    aria-label={`Remove TP ${i + 1}`}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="tp-add-row-btn"
                style={{ background: `${theme.text}14`, color: theme.text }}
                disabled={tpFormSaving || tpFormLines.length >= MAX_TP_LEVELS}
                onClick={addTpFormRow}
              >
                + Add TP
              </button>
            </div>
            <div className="tp-form-actions">
              <button
                type="button"
                style={{ background: `${theme.text}18`, color: theme.text }}
                onClick={closeTpEditor}
                disabled={tpFormSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                style={{ background: theme.button, color: theme.buttonText }}
                onClick={() => void submitTpForm()}
                disabled={tpFormSaving}
              >
                {tpFormSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
